import { useAtom } from 'jotai';
import { MouseEventHandler, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ITableButtonProps } from '@/lib/v2/examples/TableToolsBar/components/TableToolsBarButton/TableToolsBarButton';
import {
  DownloadIcon,
  DuplicateIconNew,
  EditPencilIconNew,
  RemoveIconNew,
} from '@/lib/v2/icons/solid';

import {
  atomDeleteModalIsOpen,
  atomDuplicateModalIsOpen,
  atomEditModalIsOpen,
} from '@/src/modules/AutomationModuleNew/atoms/automationAtoms';

interface UseActionBarProps {
  totalRowSelected?: number;
  hideDeleteButton?: boolean;
  hideEditButton?: boolean;
  hideDuplicateButton?: boolean;
  showDownloadButton?: boolean;
  onClickDownload?: MouseEventHandler<HTMLButtonElement>;
}
export const useActionBar = ({
  totalRowSelected = 0,
  hideDeleteButton = false,
  hideEditButton = false,
  hideDuplicateButton = false,
  showDownloadButton = false,
  onClickDownload,
}: UseActionBarProps = {}) => {
  const { t } = useTranslation();
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useAtom(atomDeleteModalIsOpen);
  const [editModalIsOpen, setEditModalIsOpen] = useAtom(atomEditModalIsOpen);
  const [duplicateModalIsOpen, setDuplicateModalIsOpen] = useAtom(atomDuplicateModalIsOpen);

  const toggleEditModal = useCallback(() => {
    setEditModalIsOpen((prev) => !prev);
  }, [setEditModalIsOpen]);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModalIsOpen((prev) => !prev);
  }, [setDeleteModalIsOpen]);

  const toggleDuplicateModal = useCallback(() => {
    setDuplicateModalIsOpen((prev) => !prev);
  }, [setDuplicateModalIsOpen]);

  const actions = useMemo<ITableButtonProps[]>(
    () => [
      {
        icon: <EditPencilIconNew />,
        onClick: toggleEditModal,
        tooltip: t('CAMPAIGNS_MAIN.ACTIONS.tooltipEdit'),
        hide: hideEditButton || totalRowSelected > 1,
        id: 'edit-campaign',
        label: t('CAMPAIGNS_MAIN.ACTIONS.tooltipEdit'),
      },
      {
        icon: <DuplicateIconNew />,
        onClick: toggleDuplicateModal,
        tooltip: t('CAMPAIGNS_MAIN.ACTIONS.tooltipDuplicate'),
        hide: hideDuplicateButton || totalRowSelected > 1,
        id: 'duplicate-campaign-action',
        label: t('CAMPAIGNS_MAIN.ACTIONS.tooltipDuplicate'),
      },
      {
        icon: <RemoveIconNew />,
        onClick: toggleDeleteModal,
        tooltip: t('CAMPAIGNS_MAIN.ACTIONS.tooltipDelete'),
        hide: hideDeleteButton,
        id: 'delete-campaign',
        label: t('CAMPAIGNS_MAIN.ACTIONS.tooltipDelete'),
      },
      {
        icon: <DownloadIcon />,
        onClick: onClickDownload,
        tooltip: t('AUTOMATION_MAIN_MODULE.downloadReport'),
        id: 'download',
        hide: !showDownloadButton || totalRowSelected > 1,
        label: t('AUTOMATION_MAIN_MODULE.downloadReport'),
      },
    ],
    [
      toggleEditModal,
      t,
      hideEditButton,
      totalRowSelected,
      toggleDuplicateModal,
      hideDuplicateButton,
      toggleDeleteModal,
      hideDeleteButton,
      onClickDownload,
      showDownloadButton,
    ]
  );

  return {
    actions,
    deleteModalIsOpen,
    toggleDeleteModal,
    editModalIsOpen,
    toggleEditModal,
    duplicateModalIsOpen,
    toggleDuplicateModal,
  };
};
